// Media URLs chosen by a puzzle's creator are opened by its recipient on our
// origin (and legacy links carry the whole config in the hash, with no database
// in between). They are untrusted: a `javascript:` URL in an <iframe src> runs
// with our origin. Everything a creator can type or upload goes through here
// before it reaches a src attribute; anything not explicitly allowed becomes "".

import { isNativeApp } from "../services/platform";

// In the native app the page is capacitor://localhost and YouTube refuses the
// embed (error 153, iPhone, 21/10): the video goes through public/yt.html, which
// is served over https (PR #94).
export const YT_PAGINA = `${(process.env.REACT_APP_PUBLIC_URL || "https://puzzly.giadalabs.com").replace(/\/$/, "")}/yt.html`;

// Id length {6,20} matches public/yt.html, so web and native accept the same ids.
const YT_RE = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{6,20})(?![a-zA-Z0-9_-])/;

/**
 * Turns a YouTube link into the URL to put in an <iframe src>, or "" for anything
 * else. The result is always built from the video id alone, so nothing from the
 * input (scheme, host, query) can reach the frame. YouTube is the only provider
 * the creator offers.
 */
export function safeEmbedUrl(url) {
  if (typeof url !== "string" || !url) return "";
  const yt = url.match(YT_RE);
  if (!yt) return "";
  return isNativeApp() ? `${YT_PAGINA}?v=${yt[1]}` : `https://www.youtube.com/embed/${yt[1]}`;
}

// Photos picked in the classic builder are compressed to a canvas JPEG data URL
// (compressImage in App.jsx). Only raster types, only base64, nothing after it.
const DATA_IMAGE_RE = /^data:image\/(?:png|jpeg|gif|webp);base64,[A-Za-z0-9+/]+={0,2}$/;

// Songs picked in the classic builder before #53 were stored with readAsDataURL,
// so legacy puzzles and hash links carry base64 audio. A data: URL in <audio>
// cannot run script.
const DATA_AUDIO_RE = /^data:audio\/(?:mpeg|mp3|mp4|x-m4a|aac|wav|x-wav|ogg|webm);base64,[A-Za-z0-9+/]+={0,2}$/;

const KINDS =new Set(["image", "audio", "video"]);

/**
 * A direct media file URL (img/audio/video src) or "". Allowed: https URLs; for
 * images only, base64 PNG/JPEG/GIF/WebP data URLs; for audio only, base64 data
 * URLs of the common audio types (legacy songs).
 */
export function safeMediaUrl(url, kind) {
  if (typeof url !== "string" || !url || !KINDS.has(kind)) return "";
  if (kind === "image" && DATA_IMAGE_RE.test(url)) return url;
  if (kind === "audio" && DATA_AUDIO_RE.test(url)) return url;
  // No whitespace (incl. NBSP) or control characters anywhere: browsers strip
  // some of them and the scheme check must see exactly what the browser sees.
  // eslint-disable-next-line no-control-regex
  if (/[\s\u0000-\u001f\u007f]/.test(url)) return "";
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return "";
  }
  return parsed.protocol === "https:" ? url : "";
}
